import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, LinearProgress, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import presentationUsageTracker, { FREE_PLAN_MONTHLY_LIMIT } from '../services/presentation-usage-tracker';
import { isPremiumSubscription } from '../services/subscription-check-service';

/**
 * PresentationLimitBanner Component
 * -------------------------
 * This component displays a banner when a free user is approaching or has reached
 * the monthly presentation creation limit, encouraging them to upgrade to premium.
 * It also shows detailed usage statistics for the last 30 days.
 * 
 * @param {Object} props - Component props
 * @param {number} props.currentUsage - Current number of presentations created in the last 30 days
 * @returns {JSX.Element|null} The banner component or null if not needed
 */
export default function PresentationLimitBanner({ currentUsage }) {
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Fetch detailed usage statistics
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const stats = await presentationUsageTracker.getUsageStatistics();
        setUsageStats(stats);
      } catch (error) {
        console.error('Error fetching presentation usage stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [currentUsage]);
  
  // Don't show for premium users or if usage is 0
  if (isPremiumSubscription() || currentUsage === 0) {
    return null;
  }

  const remaining = Math.max(0, FREE_PLAN_MONTHLY_LIMIT - currentUsage);
  const limitReached = remaining === 0;
  
  // Calculate percentage of limit used
  const percentUsed = Math.min(100, (currentUsage / FREE_PLAN_MONTHLY_LIMIT) * 100);
  
  // Determine banner style and content based on status
  const severity = limitReached ? 'error' : 'warning';
  const title = limitReached ? 
    '🔒 Monthly Presentation Limit Reached' : 
    '⚠️ Monthly Presentation Limit';
  
  const message = limitReached ?
    `You've reached the limit of ${FREE_PLAN_MONTHLY_LIMIT} presentations this month on the free plan. Upgrade to Premium for unlimited presentations.` :
    `You have created ${currentUsage} of ${FREE_PLAN_MONTHLY_LIMIT} presentations allowed this month. Upgrade to Premium for unlimited presentations.`;

  return (
    <Box sx={{ mb: 3 }}>
      <Alert 
        severity={severity}
        action={
          <Button 
            color="inherit" 
            size="small" 
            component={Link} 
            to="/subscriptions"
            variant="outlined"
          >
            Upgrade
          </Button>
        }
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={percentUsed} 
            sx={{ 
              flexGrow: 1, 
              mr: 2, 
              height: 8, 
              borderRadius: 5,
              backgroundColor: limitReached ? 'rgba(211, 47, 47, 0.2)' : 'rgba(237, 108, 2, 0.2)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: limitReached ? '#d32f2f' : '#ed6c02'
              }
            }} 
          />
          <Typography variant="body2" fontWeight="medium">
            {currentUsage}/{FREE_PLAN_MONTHLY_LIMIT}
          </Typography>
        </Box>
        
        <Typography variant="body2">
          {message}
        </Typography>
        
        {!limitReached && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {remaining} {remaining === 1 ? 'presentation' : 'presentations'} remaining this month.
          </Typography>
        )}
        
        {loading ? (
          <LinearProgress sx={{ mt: 1, height: 2 }} />
        ) : usageStats && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {usageStats.today > 0 && (
              <Chip 
                size="small" 
                label={`Today: ${usageStats.today}`} 
                color="primary" 
                variant="outlined" 
              />
            )}
            {usageStats.total7Days > 0 && (
              <Chip 
                size="small" 
                label={`Last 7 days: ${usageStats.total7Days}`} 
                color="primary" 
                variant="outlined" 
              />
            )}
            <Chip 
              size="small" 
              label={`Last 30 days: ${usageStats.total30Days}`} 
              color="primary" 
              variant="outlined" 
            />
          </Box>
        )}
      </Alert>
    </Box>
  );
}
